import { Fragment, useState, type JSX } from 'react';
import { Button, Icon, Label, Select, SelectOption, useToast } from '@wistia/ui';
import { stringHelpers } from '@wistia/vhs';
import { DOWNLOAD_OPTIONS } from '~/singlePageApp/screens/MediaCustomize/Customize/AudioDescriptions/DownloadExtendedAudioDescriptionFilesModal';
import type { ExtendedAudioDescriptionMp3File } from '~/graphql';
import { useDownloadExtendedAudioDescriptionFileMutation } from '~/singlePageApp/screens/MediaCustomize/Customize/AudioDescriptions/__gql/ExtendedAudioDescriptionMp3Files.gql.gen';
import { isNonEmptyString, isNotNil } from '~/utilities/type-guards';
import { trackStructEvent } from '~/utilities/snowplow';
import { downloadFile } from '~/utilities/downloadFile';
import { SelectWrapper, InlineSelectWrapper } from './SharedStyledComponents';

type Props = {
  extendedAudioFiles: ExtendedAudioDescriptionMp3File[];
};

export const ExtendedAudioDescriptionDownload = ({ extendedAudioFiles }: Props): JSX.Element => {
  const showToast = useToast();
  const [selectedFormat, setSelectedFormat] = useState('mp3');
  const [audioDescriptionId, setaudioDescriptionId] = useState(
    extendedAudioFiles[0]?.mediaExtendedAudioDescription?.threeplayAudioDescription?.id ?? '',
  );

  const onChange = (value: string) => {
    const [format, threeplayId] = value.split('|');
    setSelectedFormat(format);
    setaudioDescriptionId(threeplayId);
  };

  const [downloadExtendedAudioDescriptionFile] = useDownloadExtendedAudioDescriptionFileMutation();

  const handleDownload = () => {
    if (stringHelpers.isUrl(audioDescriptionId)) {
      window.open(audioDescriptionId, '_blank');

      trackStructEvent({
        category: 'button',
        action: 'click',
        label: 'publish-download-audio-files-for-video',
      });

      return;
    }

    void downloadExtendedAudioDescriptionFile({
      variables: {
        input: {
          audioDescriptionId,
          format: selectedFormat,
        },
      },
      onCompleted: (response) => {
        if (isNotNil(response.downloadExtendedAudioDescriptionFile)) {
          const { downloadUrl, fileData } = response.downloadExtendedAudioDescriptionFile;

          if (isNonEmptyString(downloadUrl)) {
            window.open(downloadUrl, '_blank');
          } else {
            downloadFile(fileData, `${selectedFormat}.vtt`, 'text/plain');
          }

          trackStructEvent({
            category: 'button',
            action: 'click',
            label: 'publish-download-audio-files-for-video',
          });
        } else {
          showToast({
            message: 'Cannot download file. Please contact customer support.',
            colorScheme: 'error',
          });
        }
      },
    });
  };

  return (
    <SelectWrapper>
      <Label>Extended audio descriptions</Label>
      <InlineSelectWrapper>
        <Select
          fullWidth={true}
          onChange={onChange}
          value={`${selectedFormat}|${audioDescriptionId}`}
        >
          {extendedAudioFiles.map(({ id, mediaExtendedAudioDescription, ordered }) => {
            const languageName = mediaExtendedAudioDescription?.languageName;
            return ordered ? (
              <Fragment key={id}>
                {DOWNLOAD_OPTIONS.map(({ name, format }) => (
                  <SelectOption
                    key={format}
                    value={`${format}|${mediaExtendedAudioDescription?.threeplayAudioDescription?.id}`}
                  >
                    {languageName} - {name}
                  </SelectOption>
                ))}
              </Fragment>
            ) : (
              <SelectOption
                key={id}
                value={`mp3|${mediaExtendedAudioDescription?.providedMediaFile?.downloadUrl}`}
              >
                {languageName} - mp3
              </SelectOption>
            );
          })}
        </Select>

        <Button
          onClick={handleDownload}
          leftIcon={<Icon type="download" />}
          data-testid="download-xad"
        >
          Download
        </Button>
      </InlineSelectWrapper>
    </SelectWrapper>
  );
};
